import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  CONTRACT_ALERT_LABELS,
  CONTRACT_ALERT_TONES,
  describeExpiry,
  dismissContractAlerts,
  listPendingContractAlerts,
  type SupplierContract,
} from "@/lib/suppliersApi";

/**
 * Expiring-supplier-contract popup, shown once per app load to the CEO and General Manager.
 *
 * The backend decides what is pending: a contract appears when it crosses 1 month / 2 weeks /
 * 1 week / 3 days before expiry (or is already expired) and this user has not dismissed that
 * specific step yet. Closing the dialog acknowledges every contract in it, so the same warning
 * does not reappear until the contract slips into the next, more urgent step.
 */
export const ContractExpiryAlertDialog = () => {
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<SupplierContract[]>([]);
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const isAlertedRole = userRole === "ceo" || userRole === "general_manager";

  useEffect(() => {
    if (!isAlertedRole) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await listPendingContractAlerts();
        if (cancelled || response.items.length === 0) return;
        setContracts(response.items);
        setOpen(true);
      } catch {
        // The popup is a nudge, not a critical path: stay silent if it cannot be fetched.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAlertedRole]);

  const acknowledge = async () => {
    if (dismissing) return;
    try {
      setDismissing(true);
      await dismissContractAlerts(contracts.map((contract) => contract.id));
    } catch {
      // Failing to record the dismissal only means the popup shows again next time.
    } finally {
      setDismissing(false);
      setOpen(false);
    }
  };

  const handleReview = async () => {
    await acknowledge();
    navigate("/suppliers");
  };

  if (!isAlertedRole || contracts.length === 0) {
    return null;
  }

  const expiredCount = contracts.filter((contract) => (contract.days_until_expiry ?? 0) < 0).length;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) void acknowledge(); }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            {contracts.length === 1
              ? "1 supplier contract needs attention"
              : `${contracts.length} supplier contracts need attention`}
          </DialogTitle>
          <DialogDescription>
            {expiredCount > 0
              ? `${expiredCount} of these ${expiredCount === 1 ? "has" : "have"} already expired.`
              : "These contracts are approaching their expiry date."}{" "}
            Closing this marks them as seen until they reach the next alert step.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-80 space-y-2 overflow-y-auto">
          {contracts.map((contract) => (
            <div key={contract.id} className="flex items-start justify-between gap-3 rounded-lg border px-4 py-3">
              <div className="min-w-0">
                <p className="truncate font-medium">{contract.supplier_name}</p>
                <p className="truncate text-sm text-muted-foreground">
                  {contract.title || contract.file_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {describeExpiry(contract)}
                  {contract.expiry_date ? ` · ${contract.expiry_date}` : ""}
                </p>
              </div>
              {contract.alert_threshold && (
                <Badge variant="outline" className={`shrink-0 ${CONTRACT_ALERT_TONES[contract.alert_threshold]}`}>
                  {CONTRACT_ALERT_LABELS[contract.alert_threshold]}
                </Badge>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => { void acknowledge(); }} disabled={dismissing}>
            {dismissing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Dismiss
          </Button>
          <Button type="button" onClick={() => { void handleReview(); }} disabled={dismissing}>
            Review suppliers
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContractExpiryAlertDialog;
